import React, {useEffect, useState, useCallback, useContext, createContext} from 'react';

import {useAsync, useDebounce} from '@bootcamp/shared/src/util/hooks';
import {searchQuestions, getQuestionMastery} from '@bootcamp/shared/src/requests';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';

const DAT_TEST_TAG_ID = '371eb193-7e9e-442c-88c1-82139e559912';
const INBDE_TEST_TAG_ID = '3022e66b-64f9-46ef-abba-392864efe3fe';

// Create the context
const QuestionSearch = createContext();

// Create a provider component
const QuestionSearchProvider = ({ children }) => {
  const {bootcamp, DEFAULT_USER_ID} = useUserDataContext();
  const [searchParams, setSearchParams] = useState({
    filterRequired: true,
    filter: '',
    limit: 25,
    sort: {},
    nextToken: null,
    filterOverride: null
  });

  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const composeFilterOverride = filter => {
    return {and: [{searchString: {matchPhrasePrefix: filter?.toLowerCase()}}, {or: [{searchString: {matchPhrase: `[test:${bootcamp === 'inbde' ? INBDE_TEST_TAG_ID : DAT_TEST_TAG_ID}]`}}, {searchString: {matchPhrase: `[bootcamp:${bootcamp === 'inbde' ? INBDE_TEST_TAG_ID : DAT_TEST_TAG_ID}]`}}]}]}
  }

  const debouncedSearchFilter = useDebounce(searchParams.filter, 500);

  const fetchFilteredQuestions = async () => {
    const {filter, filterOverride, filterRequired, limit, sort} = searchParams;
    if (!(filter || filterOverride) && filterRequired) return setLoadingQuestions(false);
    setLoadingQuestions(true);
    const input = {
      ...(filterOverride ?
        {filter: filterOverride, limit, sort}
        : filter ?
        {filter: {searchString: {matchPhrasePrefix: filter.toLowerCase()}}}
        : {}),
        limit,
        sort,
        nextToken: searchParams.nextToken
      };
      const {items: fetchedQuestions, nextToken} = await searchQuestions(input);

      setSearchParams({...searchParams, nextToken});
      setHasNextPage(nextToken && fetchedQuestions.length === limit);

      setFilteredQuestions([...filteredQuestions, ...fetchedQuestions]);
      setLoadingQuestions(false);
  }

  const asyncFetch = useCallback(fetchFilteredQuestions, [debouncedSearchFilter]);
  const {pending: loadingNextPage} = useAsync(asyncFetch);

  const getResults = (filter) => {
    setSearchParams({...searchParams, filter, filterOverride: composeFilterOverride(filter), nextToken: null})
    setFilteredQuestions([]);
  };

  const loadNextPage = async () => {
    return await fetchFilteredQuestions();
  };

  useEffect(() => {
    setSearchParams({...searchParams, filter: '', filterRequired: true, sort: {}, nextToken: null});
  }, [setSearchParams]);

  // Define any functions or state updates here
  const value = {
    results: filteredQuestions,
    loadingNextPage,
    loadingQuestions,
    hasNextPage,
    getResults,
    loadNextPage,
  };

  return (
    <QuestionSearch.Provider value={value}>
      {children}
    </QuestionSearch.Provider>
  );
};



const useQuestionSearchContext = () => useContext(QuestionSearch);

export {useQuestionSearchContext, QuestionSearchProvider};